import React, { useState, useEffect, useMemo } from 'react'
import { useFormState, useFormApi } from 'informed'
import { Row, Col } from 'react-flexbox-grid'
import styled from 'styled-components'
import moment from 'moment'

import { DateTimePicker, Select, Text } from 'components/forms'
import { CardItemList } from 'components/Card'
import Button from 'components/Button'

const Schedule = ({ data, newRecord, t, language }) => {
	const [schedule, setSchedule] = useState({
		time: data?.time || [],
		weekDay: data?.weekDay || [],
		date: data?.date || [],
		month: data?.month || []
	})
	const [selectedTime, setSelectedTime] = useState(null)
	const [timeError, setTimeError] = useState()
	const [errors, setErrors] = useState({ time: false, day: false })
	const formApi = useFormApi()
	const formState = useFormState()

	useEffect(() => {
		if(newRecord && formState.dirty) {
			handleSave()
		}
	}, [schedule])

	const handleSave = () => {
		const isValid = passValidation(schedule)
		const isTimeSet = schedule?.time?.length
		if(isValid && isTimeSet) {
			formApi.setError('params.schedule', null)
			formApi.setValue('params.schedule', schedule)
			if(newRecord) {
				formApi.submitForm()
			}
		} else {
			formApi.setError('params.schedule', t('validators.invalid_value'))
		}
		if(!isTimeSet) {
			setTimeError(true)
		}
	}

	const handleChange = type => val => {
		const newVal = Array.isArray(val) ? val.map(({ value }) => value) : []
		const newSchedule = {
			...schedule,
			[type]: newVal
		}
		setSchedule(newSchedule)
		if(type === 'weekDay' || type === 'date') {
			setErrors({ ...errors, day: !passValidation(newSchedule) })
		}
	}

	const passValidation = ({ weekDay, date }) => !(weekDay?.length && date?.length) // weekDay and date can't be set at one time

	const timeItems = useMemo(() => schedule?.time?.reduce?.((outArr, item) => {
		if(item) {
			const [hour, minute] = item.split(':')
			const date = new Date()
			date.setUTCHours(hour)
			date.setUTCMinutes(minute)
			outArr.push({ label: moment(date).format('HH:mm'), value: item })
		}
		return outArr
	}, []), [schedule?.time])
	// Different times can be selelcted only with same minutes value
	const specificTimes = useMemo(() => {
		if(!schedule?.time || !schedule?.time?.length) {
			return {}
		}
		const mins = schedule?.time?.[0].split(':')[1] || 0
		const hoursArr = schedule?.time?.map(time => Number(time.split(':')[0]))
		const enabledVals = []
		for(let i = 0; i < 24; i++) {
			if(hoursArr.indexOf(i) === -1) {
				enabledVals.push(moment.utc().set({ hour: i, minute: mins, seconds: 0 }).local())
			}
		}
		const nonStandartMins = Number(mins) % 15 !== 0
		return {
			includeTimes: enabledVals,
			...nonStandartMins && { injectTimes: enabledVals }
		}
	}, [schedule?.time])
	const weekDaysOptions = useMemo(() => ([
		{ value: 2, label: t('common.dates.monday') },
		{ value: 3, label: t('common.dates.tuesday') },
		{ value: 4, label: t('common.dates.wednesday') },
		{ value: 5, label: t('common.dates.thursday') },
		{ value: 6, label: t('common.dates.friday') },
		{ value: 7, label: t('common.dates.saturday') },
		{ value: 1, label: t('common.dates.sunday') }
	]), [t])
	const monthOptions = useMemo(() => ([
		{ value: 1, label: t('common.dates.january') },
		{ value: 2, label: t('common.dates.february') },
		{ value: 3, label: t('common.dates.march') },
		{ value: 4, label: t('common.dates.april') },
		{ value: 5, label: t('common.dates.may') },
		{ value: 6, label: t('common.dates.june') },
		{ value: 7, label: t('common.dates.july') },
		{ value: 8, label: t('common.dates.august') },
		{ value: 9, label: t('common.dates.september') },
		{ value: 10, label: t('common.dates.october') },
		{ value: 11, label: t('common.dates.november') },
		{ value: 12, label: t('common.dates.december') }
	]), [t])
	const dateOptions = useMemo(() => getDates(), [])

	const scheduleText = useMemo(() => {
		const { weekDay, date, month } = schedule
		if(!timeItems.length && !weekDay.length && !date.length && !month.length) {
			return ''
		}
		let daysText = t('common.dates.daily')
		const textEvery = t('options_section.strings.every')
		if(weekDay.length) {
			const weekDays = weekDay.map(value => weekDaysOptions.find(item => item.value === value)?.label).join(', ')
			daysText = `${textEvery} ${weekDays}`
		}
		if(date.length) {
			const dates = date.map(val => {
				switch(language.toLowerCase()) {
					case 'german': return val + '.'
					case 'english': return val + 'th'
					default: return val
				}
			}).join(', ')
			daysText = `${textEvery} ${dates}`
		}
		let monthsText = ''
		if(month.length) {
			const monthNames = month.map(value => monthOptions.find(item => item.value === value)?.label).join(', ')
			monthsText = ` ${t('common.strings.in')} ${monthNames}`
		}
		const times = timeItems.map(({ label }) => label).join(', ')
		const timeText = times.length ? ` ${t('common.strings.at')} ${times}` : ''
		return daysText + monthsText + timeText
	}, [schedule])

	const handleAddTime = () => {
		if(selectedTime) {
			// Use selected UTC value as value in local time zone
			const timeZoneOffset = moment().utcOffset()
			const newVal = moment.utc(selectedTime).utcOffset(timeZoneOffset, true).utc().format('HH:mm:ssZ')
			setSchedule({
				...schedule,
				time: [...schedule.time, newVal]
			})
			setSelectedTime(null)
			if(timeError) {
				setTimeError(false)
			}
		}
	}
	const handleDeleteTime = val => {
		const newTimes = schedule.time.filter(item => item !== val?.value)
		setSchedule({
			...schedule,
			time: newTimes
		})
	}

	return(
		<>
			<div style={{ display: 'none' }}><Text field='params.schedule' hidden validate={val => val ? null : t('validators.invalid_value')} validateOnChange /></div>
			<div>
				<p style={{ display: 'inline-block', marginRight: '10px' }}>{t('options_section.strings.schedule_execution')}</p>
				{formState?.errors?.params?.schedule && <StyledError>{t('validators.invalid_value')}</StyledError>}
			</div>
			<div style={{ marginBottom: '10px', color: 'green' }}>
				{!newRecord && <Button text={`${t('common.buttons.save')} ${t('options_section.strings.schedule_execution')}`} onClick={handleSave} style={{ marginRight: '15px' }} />}
				<span>{scheduleText}</span>
			</div>
			<Row>
				<Col xs={2}>
					<DateTimePicker
						noForm
						value={selectedTime}
						onChange={val => setSelectedTime(val)}
						picker='time'
						label={t('common.fields.time')}
						{...specificTimes}
						timeCaption='Zeit'
					/>
					<Button
						text='↓'
						status='cancelation'
						onClick={handleAddTime}
						style={{
							minWidth: '50px',
							margin: '0 auto 18px',
							display: 'block',
							color: 'green',
							fontSize: '22px'
						}}
					/>
					<CardItemList
						items={timeItems.length ? timeItems : [{ label: t('common.strings.table_no_data') }]}
						onRemoveItem={handleDeleteTime}
						label={t('common.fields.time')}
						ItemComponent={({ item }) => item.label}
					/>
					{timeError &&
						<div>
							<StyledError>{t('validators.required')}</StyledError>
						</div>
					}
				</Col>
				<Col xs={3}>
					<Select
						noForm
						value={schedule?.weekDay?.map?.(value => weekDaysOptions.find(item => item.value === value))}
						onChange={handleChange('weekDay')}
						label={t('options_section.fields.days_of_week')}
						options={weekDaysOptions}
						{...errors.day && { error: t('options_section.strings.select_day_or_month') }}
						usePlaceholder
						placeholder={t('common.dates.daily')}
						isMulti
						closeMenuOnSelect={false}
						hideSelectedOptions={false}
					/>
				</Col>
				<Col xs>
					<Select
						noForm
						value={schedule?.date?.map?.(value => dateOptions.find(item => item.value === value))}
						onChange={handleChange('date')}
						label={t('options_section.fields.month_days')}
						options={dateOptions}
						{...errors.day && { error: t('options_section.strings.select_day_or_month') }}
						usePlaceholder
						placeholder={t('common.dates.daily')}
						isMulti
						closeMenuOnSelect={false}
						hideSelectedOptions={false}
					/>
				</Col>
				<Col xs>
					<Select
						noForm
						value={schedule?.month?.map?.(value => monthOptions.find(item => item.value === value))}
						onChange={handleChange('month')}
						label={t('common.dates.months')}
						options={monthOptions}
						usePlaceholder
						placeholder={t('options_section.fields.monthly')}
						isMulti
						closeMenuOnSelect={false}
						hideSelectedOptions={false}
					/>
				</Col>
			</Row>
		</>
	)
}

const getDates = () => [...Array(31)].map((item, ind) => ({ value: ind + 1, label: ind + 1 }))

const StyledError = styled.span`
	font-size: 11px;
	color: ${({ theme }) => theme?.errorColor};
`

export default Schedule
